import { falAngleLeft } from 'shared/@generated/icons/falAngleLeft';
import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { View } from 'react-native';
import React, { useMemo } from 'react';
import { PaginationProps } from 'shared/components/pagination/index';
import { Button } from 'shared/components/button';
import { useRouter } from 'shared/library/use-router';
import { H2 } from 'shared/components/title';
import catSad from 'shared/assets/doodles/cat-sad.png';
import { Image } from 'shared/components/image';
import { useTranslation } from 'react-i18next';
import { Text } from 'shared/components/text';

import { AdBanner } from 'shared/components/ad-banner';
import { buildPaginationHref } from './pagination-url';

// Leave room between cards so a GridInsert and recurring ad keep their existing order.
const PAGINATION_ORDER_STEP = 10;

function getEffectiveAdInterval(
  showAds: boolean,
  requestedInterval: number | undefined,
  columns: number,
) {
  if (!showAds) {
    return null;
  }

  const interval = requestedInterval ?? columns * 2;

  if (interval <= 0) {
    return null;
  }

  return Math.max(columns, Math.ceil(interval / columns) * columns);
}

export function Pagination<T extends { id: number }>({
  paginator,
  renderItem,
  renderLoadingPlaceholder: _renderLoadingPlaceholder,
  loadingPlaceholderCount: _loadingPlaceholderCount,
  onPageChange: _onPageChange,
  showAds = false,
  adInterval,
  mobileAdInterval,
  showPagination = true,
  cols = 1,
  mdCols = 3,
  lgCols,
  xlCols,
  gap = 3,
  mdGap = 6,
  lgGap = 6,
  xlGap = 6,
  Header,
  ListHeader,
  GridInsert,
  gridInsertAfterRows = 1,
  ListFooter,
  noResultsTitle,
  noResultsSubtitle,
}: PaginationProps<T>) {
  const { t } = useTranslation('components');
  const { asPath } = useRouter();

  lgCols = lgCols ?? mdCols;
  xlCols = xlCols ?? lgCols;

  const responsiveAdPlacements = [
    {
      key: 'mobile',
      className: 'min-[769px]:hidden',
      interval: getEffectiveAdInterval(showAds, mobileAdInterval ?? adInterval, cols),
    },
    {
      key: 'md',
      className: 'hidden min-[769px]:block min-[1025px]:hidden',
      interval: getEffectiveAdInterval(showAds, adInterval, mdCols),
    },
    {
      key: 'lg',
      className: 'hidden min-[1025px]:block min-[1281px]:hidden',
      interval: getEffectiveAdInterval(showAds, adInterval, lgCols),
    },
    {
      key: 'xl',
      className: 'hidden min-[1281px]:block',
      interval: getEffectiveAdInterval(showAds, adInterval, xlCols),
    },
  ];

  const pages = useMemo(() => {
    const windowSize = 3;
    let start = Math.max(1, paginator.current_page - Math.floor(windowSize / 2));
    const end = Math.min(paginator.last_page, start + windowSize - 1);

    start = Math.max(1, end - windowSize + 1);

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [paginator]);

  const normalizedGridInsertRows = Math.max(1, gridInsertAfterRows);
  const gridInsertItemCounts = [
    cols * normalizedGridInsertRows,
    mdCols * normalizedGridInsertRows,
    lgCols * normalizedGridInsertRows,
    xlCols * normalizedGridInsertRows,
  ] as const;
  const firstGridInsertItemCount = Math.min(...gridInsertItemCounts);
  const gridInsertVisibility = [
    paginator.data.length >= gridInsertItemCounts[0] ? 'block' : 'hidden',
    paginator.data.length >= gridInsertItemCounts[1] ? 'min-[769px]:block' : 'min-[769px]:hidden',
    paginator.data.length >= gridInsertItemCounts[2] ? 'min-[1025px]:block' : 'min-[1025px]:hidden',
    paginator.data.length >= gridInsertItemCounts[3] ? 'min-[1281px]:block' : 'min-[1281px]:hidden',
  ].join(' ');
  const hasLeadingAd = showAds && paginator.data.length > 0;
  const hasListHeader = Boolean(Header || ListHeader);

  const renderGridItems = () => {
    return paginator.data.flatMap((item, index) => {
      const itemCount = index + 1;
      const items = [
        <div
          key={`item-${item.id}-${index}`}
          className={'pagination-ordered-item'}
          style={{ order: index * PAGINATION_ORDER_STEP }}
        >
          {renderItem(item)}
        </div>,
      ];

      if (GridInsert && firstGridInsertItemCount === itemCount) {
        items.push(
          <div
            key={`grid-insert-after-${index}`}
            className={`pagination-responsive-insert ${gridInsertVisibility}`}
            style={
              {
                '--insert-order': gridInsertItemCounts[0] * PAGINATION_ORDER_STEP - 2,
                '--md-insert-order': gridInsertItemCounts[1] * PAGINATION_ORDER_STEP - 2,
                '--lg-insert-order': gridInsertItemCounts[2] * PAGINATION_ORDER_STEP - 2,
                '--xl-insert-order': gridInsertItemCounts[3] * PAGINATION_ORDER_STEP - 2,
              } as any
            }
          >
            {GridInsert()}
          </div>,
        );
      }

      responsiveAdPlacements.forEach((placement) => {
        if (
          placement.interval &&
          itemCount % placement.interval === 0 &&
          itemCount < paginator.data.length
        ) {
          items.push(
            <div
              key={`ad-${placement.key}-after-${item.id}-${index}`}
              className={`pagination-ordered-ad ${placement.className}`}
              style={{ order: itemCount * PAGINATION_ORDER_STEP - 1 }}
            >
              <AdBanner placement={'content'} />
            </div>,
          );
        }
      });

      return items;
    });
  };

  return (
    <View>
      {hasLeadingAd && (
        <AdBanner
          className={hasListHeader ? 'mb-6 mt-4 md:mt-6' : 'mt-4 md:mt-6'}
          placement={'top'}
        />
      )}

      {Header && Header()}

      {ListHeader && ListHeader()}

      <div
        className="pagination-grid mt-3 md:mt-6"
        style={
          {
            '--cols': cols,
            '--md-cols': mdCols,
            '--lg-cols': lgCols ?? mdCols,
            '--xl-cols': xlCols ?? lgCols ?? mdCols,
            '--gap': `${gap * 0.25}rem`,
            '--md-gap': `${mdGap * 0.25}rem`,
            '--lg-gap': `${lgGap * 0.25}rem`,
            '--xl-gap': `${xlGap * 0.25}rem`,
          } as any
        }
      >
        {renderGridItems()}
      </div>

      {paginator.data.length === 0 && (
        <View className={'items-center justify-center p-6 md:pt-20'}>
          <Image
            src={catSad}
            alt={t('pagination.no_results_found')}
            containerClassName={'bg-brand-cream mb-3 size-32 rounded-full p-4'}
            imageClassName={'object-contain'}
          />

          <H2
            title={noResultsTitle ?? t('pagination.no_results_found')}
            subtitle={noResultsSubtitle ?? t('pagination.we_could_not_find_any_results')}
            marginTop={false}
            center={true}
          />
        </View>
      )}

      {paginator.data.length > 0 && showPagination && (
        <>
          <View className={'mt-6 flex-row items-center justify-center space-x-3 md:mt-12'}>
            {paginator.prev_page_url ? (
              <Button
                text={t('pagination.previous')}
                color={'white'}
                icon={falAngleLeft}
                iconPosition="left"
                dense={true}
                href={buildPaginationHref(asPath, paginator.current_page - 1)}
              />
            ) : (
              <Button
                text={t('pagination.previous')}
                color={'white'}
                icon={falAngleLeft}
                iconPosition="left"
                dense={true}
                disabled={true}
                onPress={() => undefined}
              />
            )}

            {pages.map((page) => (
              <Button
                key={page}
                className={page !== paginator.current_page ? 'hidden sm:flex' : undefined}
                text={page.toString()}
                color={page === paginator.current_page ? 'brown' : 'white'}
                dense={true}
                disabled={page > paginator.last_page}
                href={buildPaginationHref(asPath, page)}
              />
            ))}

            {paginator.next_page_url ? (
              <Button
                text={t('pagination.next')}
                color={'white'}
                icon={falAngleRight}
                iconPosition="right"
                dense={true}
                href={buildPaginationHref(asPath, paginator.current_page + 1)}
              />
            ) : (
              <Button
                text={t('pagination.next')}
                color={'white'}
                icon={falAngleRight}
                iconPosition="right"
                dense={true}
                disabled={true}
                onPress={() => undefined}
              />
            )}
          </View>

          <Text className={'mt-3 text-center text-sm text-stone-500'}>
            {t('pagination.showing_results', {
              from: paginator.from,
              to: paginator.to,
              total: paginator.total,
            })}
          </Text>
        </>
      )}

      {ListFooter && ListFooter()}
    </View>
  );
}
